import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ConfigService } from 'src/app/services/config.service';
import { WidgetService } from 'src/app/services/widget.service';
import { DialogService } from '../../../services/dialog.service';

interface CellPosition {
  rowIndex: number;
  column: string;
}

@Component({
    selector: 'app-csv-viewer',
    templateUrl: './csv-viewer.component.html',
    styleUrls: ['./csv-viewer.component.scss'],
    standalone: false
})
export class CsvViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  private dialogs = inject(DialogService);
  private paginator: MatPaginator;
  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
  }
  @ViewChild('tableContainer') tableContainer: ElementRef;

  @Input() teamName: string;
  @Input() filePath: string;
  @Input() directPath: string; // Filesystem path — uses /commander/read_csv instead of team API
  @Input() host: string = '';   // the linked computer directPath lives on ('' = the home)

  public pageSize = 10;
  public pageNumber = 1;
  public isLoading = true;
  public fetching = false;
  public displayedColumns = [];
  public dataSource = new MatTableDataSource<any>([]);
  public numPages = 0;
  /** Total data rows — what mat-paginator's `length` means. It was bound to
   *  numPages, so with 10 rows/page a 313-row file read "1–10 of 32" and the
   *  paginator refused to go past row 40 (Ben, 2026-09-14). */
  public totalRows = 0;
  public errorMessage: string = null;

  // Selection state
  public selectionStart: CellPosition | null = null;
  public selectionEnd: CellPosition | null = null;
  public isSelecting = false;

  // Column widths
  public columnWidths: { [key: string]: number } = {};

  // Resize observer
  private resizeObserver: ResizeObserver;
  private lastCalculatedPageSize = 0;
  private resizeDebounceTimer: any = null;

  constructor(private http: HttpClient, private elementRef: ElementRef, private widgetService: WidgetService) {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.optimizePageSize();
      this.load(this.pageNumber);
      this.setupResizeObserver();
    }, 0);
  }

  ngOnInit(): void {
    // Initial load moved to ngAfterViewInit after optimizePageSize
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }
  }

  private setupResizeObserver(): void {
    const element = this.elementRef.nativeElement;
    if (!element) return;

    this.resizeObserver = new ResizeObserver(() => {
      // Debounce to let layout settle before measuring
      if (this.resizeDebounceTimer) {
        clearTimeout(this.resizeDebounceTimer);
      }
      this.resizeDebounceTimer = setTimeout(() => {
        const newPageSize = this.calculateOptimalPageSize();
        // Only reload if page size changed
        if (newPageSize !== this.lastCalculatedPageSize && !this.fetching) {
          this.lastCalculatedPageSize = newPageSize;
          this.pageSize = newPageSize;
          this.isLoading = true; // Show spinner during resize reload
          if (this.paginator) {
            this.paginator._changePageSize(newPageSize);
          }
          this.load(1); // Reload from page 1 with new page size
        }
      }, 150);
    });

    this.resizeObserver.observe(element);
  }

  private calculateOptimalPageSize(): number {
    const parentHeight: number = this.elementRef.nativeElement?.offsetHeight || 300;
    const headerHeight = 30;
    const paginatorHeight = 56;
    const rowHeight = 30;
    const buffer = 10; // Extra buffer to ensure paginator isn't clipped
    const availableHeight = parentHeight - headerHeight - paginatorHeight - buffer;
    return Math.max(5, Math.floor(availableHeight / rowHeight) - 2);
  }

  load(pageNumber: number) {
    this.pageNumber = pageNumber;

    const request$ = this.directPath
      ? this.http.post(`${ConfigService.muxUrl}/commander/read_csv`, {
          path: this.directPath,
          page: this.pageNumber,
          page_size: this.pageSize,
          ...(this.host ? { host: this.host } : {})
        })
      : this.http.get(
          `${ConfigService.apiUrl}/csv/${this.teamName}/${encodeURI(this.filePath.replace(/\//g, '\\'))}/${this.pageSize}/${this.pageNumber}`
        );

    this.errorMessage = null;

    const sub = request$.subscribe({
      next: (response: any) => {
        sub.unsubscribe();

        if (!response || response.data.length === 0) {
          this.displayedColumns = ['empty-row'];
          this.dataSource.data = [{ 'empty-row': 'No Data' }];
          this.isLoading = false;
          if (response.error) {
            this.dialogs.alert(response.error);
          }
          return;
        }

        if (this.pageNumber === 1) {
          this.numPages = response.totalPages;
          this.totalRows = typeof response.totalRows === 'number'
            ? response.totalRows
            : (response.totalPages || 0) * this.pageSize; // legacy route: pages only
          this.displayedColumns = Object.keys(response.data[0]).filter((x) => !x.startsWith('field'));
        }

        this.dataSource.data = response.data;
        this.clearSelection();
        this.calculateColumnWidths();
        this.isLoading = false;
        this.fetching = false;
      },
      error: (err) => {
        sub.unsubscribe();
        this.isLoading = false;
        this.fetching = false;
        this.errorMessage = err.error?.detail || err.message || 'Failed to load CSV';
      }
    });
  }

  stringify(data) {
    if (typeof data == 'object') return JSON.stringify(data);
    return data;
  }

  public page(event: PageEvent) {
    this.fetching = true;
    let pageIndex = event.pageIndex + 1;

    if (this.pageSize !== event.pageSize) {
      this.paginator.pageIndex = 0;
      pageIndex = 1;
    }

    this.pageSize = event.pageSize;
    this.load(pageIndex);
  }

  optimizePageSize() {
    this.pageSize = this.calculateOptimalPageSize();
    this.lastCalculatedPageSize = this.pageSize;
    if (this.paginator) {
      this.paginator._changePageSize(this.pageSize);
    }
  }

  // Selection methods
  onCellMouseDown(event: MouseEvent, rowIndex: number, column: string) {
    if (column === 'empty-row') return;
    event.preventDefault();
    this.selectionStart = { rowIndex, column };
    this.selectionEnd = { rowIndex, column };
    this.isSelecting = true;
  }

  onCellMouseEnter(rowIndex: number, column: string) {
    if (!this.isSelecting || column === 'empty-row') return;
    this.selectionEnd = { rowIndex, column };
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this.isSelecting = false;
  }

  isCellSelected(rowIndex: number, column: string): boolean {
    if (!this.selectionStart || !this.selectionEnd) return false;

    const bounds = this.getSelectionBounds();
    const colIndex = this.displayedColumns.indexOf(column);

    return rowIndex >= bounds.startRow && rowIndex <= bounds.endRow &&
           colIndex >= bounds.minCol && colIndex <= bounds.maxCol;
  }

  getSelectionBounds(): { startRow: number; endRow: number; minCol: number; maxCol: number } {
    const startRow = Math.min(this.selectionStart.rowIndex, this.selectionEnd.rowIndex);
    const endRow = Math.max(this.selectionStart.rowIndex, this.selectionEnd.rowIndex);
    const startColIndex = this.displayedColumns.indexOf(this.selectionStart.column);
    const endColIndex = this.displayedColumns.indexOf(this.selectionEnd.column);
    const minCol = Math.min(startColIndex, endColIndex);
    const maxCol = Math.max(startColIndex, endColIndex);
    return { startRow, endRow, minCol, maxCol };
  }

  getCellSelectionClasses(rowIndex: number, column: string): string {
    if (!this.selectionStart || !this.selectionEnd) return '';

    const bounds = this.getSelectionBounds();
    const colIndex = this.displayedColumns.indexOf(column);

    const isSelected = rowIndex >= bounds.startRow && rowIndex <= bounds.endRow &&
                       colIndex >= bounds.minCol && colIndex <= bounds.maxCol;

    if (!isSelected) return '';

    const classes = ['selected'];
    if (rowIndex === bounds.startRow) classes.push('selection-top');
    if (rowIndex === bounds.endRow) classes.push('selection-bottom');
    if (colIndex === bounds.minCol) classes.push('selection-left');
    if (colIndex === bounds.maxCol) classes.push('selection-right');

    return classes.join(' ');
  }

  clearSelection() {
    this.selectionStart = null;
    this.selectionEnd = null;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    // Ctrl+C or Cmd+C to copy
    if ((event.ctrlKey || event.metaKey) && event.key === 'c') {
      if (this.selectionStart && this.selectionEnd) {
        event.preventDefault();
        this.copySelectionToClipboard();
      }
    }
    // Escape to clear selection
    if (event.key === 'Escape') {
      this.clearSelection();
    }
  }

  copySelectionToClipboard() {
    if (!this.selectionStart || !this.selectionEnd) return;

    const startRow = Math.min(this.selectionStart.rowIndex, this.selectionEnd.rowIndex);
    const endRow = Math.max(this.selectionStart.rowIndex, this.selectionEnd.rowIndex);
    const startColIndex = this.displayedColumns.indexOf(this.selectionStart.column);
    const endColIndex = this.displayedColumns.indexOf(this.selectionEnd.column);
    const minCol = Math.min(startColIndex, endColIndex);
    const maxCol = Math.max(startColIndex, endColIndex);

    const selectedColumns = this.displayedColumns.slice(minCol, maxCol + 1);
    const rows: string[] = [];

    for (let i = startRow; i <= endRow; i++) {
      const row = this.dataSource.data[i];
      const cells = selectedColumns.map(col => this.stringify(row[col]));
      rows.push(cells.join('\t'));
    }

    const text = rows.join('\n');
    navigator.clipboard.writeText(text);
  }

  // Column width calculation
  calculateColumnWidths() {
    if (!this.dataSource.data.length) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = '11px "Roboto Mono", Consolas, Monaco, monospace';

    const minWidth = 60;
    const maxWidth = 300;
    const padding = 16; // Cell padding (8px each side)

    for (const column of this.displayedColumns) {
      if (column === 'empty-row') continue;

      // Measure header width
      let maxContentWidth = ctx.measureText(column).width;

      // Measure data widths (sample first 100 rows for performance)
      const sampleSize = Math.min(this.dataSource.data.length, 100);
      for (let i = 0; i < sampleSize; i++) {
        const cellValue = this.stringify(this.dataSource.data[i][column]);
        const width = ctx.measureText(cellValue).width;
        maxContentWidth = Math.max(maxContentWidth, width);
      }

      this.columnWidths[column] = Math.min(Math.max(maxContentWidth + padding, minWidth), maxWidth);
    }
  }

  getColumnWidth(column: string): string {
    if (column === 'empty-row') return 'auto';
    return this.columnWidths[column] ? `${this.columnWidths[column]}px` : 'auto';
  }

  openInEditor(): void {
    const path = this.directPath || this.filePath;
    if (path && this.widgetService.grid) {
      this.widgetService.grid.$editFile({ parentId: null, path });
    }
  }
}
